import { useEffect, useRef, useState } from 'react'
import { useNavigate, useSearchParams } from 'react-router-dom'
import ResellerShell from './ResellerShell'
import Toggle from '../../components/Toggle'
import { tournamentApi } from '../../services/api'
import { useLanguage } from '../../context/LanguageContext'

/**
 * Custom Trips (CTQ) landing — two tabs:
 *   - Upcoming Trips: fully-paid/booked trips (booking-health monitoring later).
 *   - Quotes: the working pipeline (In Progress / Sent / ... / Accepted).
 * "New custom trip" creates a draft and opens the builder.
 *
 * CTQ feature branch — not deployed to production until the feature is done.
 */
const money = (n, cur = 'CAD') =>
  n == null ? '—' : new Intl.NumberFormat('en-CA', { style: 'currency', currency: cur }).format(Number(n))

// Pull City + State/Province out of the quote's geocoded destination label
// (e.g. "Sydney, Nova Scotia, Canada" -> { city: 'Sydney', state: 'Nova Scotia' }).
// The label is stored "place, region[, …], country"; we take the first segment
// as the city and the last non-country segment as the state/province. Handles
// the messy multi-admin-level labels Nominatim returns + "City, Country" (no region).
const COUNTRY_RX = /^(canada|united states( of america)?|u\.?s\.?a?\.?)$/i
function parseCityState(label) {
  if (!label) return { city: '', state: '' }
  const parts = String(label).split(',').map(s => s.trim()).filter(Boolean)
  if (!parts.length) return { city: '', state: '' }
  const city = parts[0]
  let rest = parts.slice(1)
  if (rest.length && COUNTRY_RX.test(rest[rest.length - 1])) rest = rest.slice(0, -1)
  return { city, state: rest.length ? rest[rest.length - 1] : '' }
}

// Compact row-action button (Resend / Duplicate / Cancel on the quotes list).
const aBtn = { border: '1px solid #e2e8f0', background: '#fff', color: '#475569', borderRadius: 6, fontSize: 11.5, fontWeight: 600, padding: '4px 8px', cursor: 'pointer', marginLeft: 6, verticalAlign: 'middle' }

// Whole days from today to a date (negative = past). null when no date.
const daysUntil = v => {
  if (!v) return null
  const d = new Date(String(v).slice(0, 10) + 'T00:00:00')
  if (isNaN(d)) return null
  const today = new Date(); today.setHours(0, 0, 0, 0)
  return Math.round((d - today) / 86400000)
}

// Format a date-ish value (date or datetime) as a local calendar date. Slices
// to the date part first so a datetime doesn't shift the day across timezones.
const fmtDate = v => {
  if (!v) return '—'
  const d = new Date(String(v).slice(0, 10) + 'T00:00:00')
  return isNaN(d) ? '—' : d.toLocaleDateString(undefined, { year: 'numeric', month: 'short', day: 'numeric' })
}

const thc = { textAlign: 'left', padding: '8px 12px', fontSize: 12, color: '#64748b', fontWeight: 600 }

// Match a quote against the search box: customer name, email, phone (also the
// quote number + trip name for convenience). Phone matches on digits only so
// "902555" finds "(902) 555-…".
function matchesSearch(q, needle) {
  if (!needle) return true
  const hay = [q.masterName, q.masterFirstName, q.masterLastName, q.masterEmail, q.masterPhone, q.quoteNumber, q.name]
    .filter(Boolean).join(' ').toLowerCase()
  if (hay.includes(needle)) return true
  const digits = needle.replace(/\D/g, '')
  const phone = (q.masterPhone || '').replace(/\D/g, '')
  return !!(digits && phone && phone.includes(digits))
}

const STATUS = {
  requested: ['#ede9fe', '#6d28d9', 'New request'],
  in_progress: ['#f1f5f9', '#475569', 'In progress'], sent: ['#dbeafe', '#1e40af', 'Sent'],
  change_requested: ['#fef3c7', '#92400e', 'Change requested'], accepted_partial: ['#dcfce7', '#166534', 'Accepted (partial)'],
  accepted_paid: ['#dcfce7', '#166534', 'Accepted (paid)'], expired: ['#f1f5f9', '#94a3b8', 'Expired'], declined_cancelled: ['#fee2e2', '#991b1b', 'Cancelled'],
}

// ── Custom Trips list columns (user-customizable) ────────────────────────────
// Canonical ordered column set + default visibility. `def:false` = offered in the
// picker but hidden until the user turns it on, so existing resellers keep today's
// columns. The Actions column is always-on and NOT part of this set.
const ALL_COLUMNS = [
  { key: 'quote',        def: true  },
  { key: 'trip',         def: true  },
  { key: 'customer',     def: true  },
  { key: 'city',         def: true  },
  { key: 'region',       def: true  },
  { key: 'country',      def: false },
  { key: 'dateReceived', def: false },
  { key: 'dateBooked',   def: true  },
  { key: 'tripDate',     def: true  },
  { key: 'total',        def: true  },
  { key: 'paid',         def: true  },
  { key: 'currency',     def: false },
  { key: 'status',       def: true  },
]
const defaultCols = () => ALL_COLUMNS.map(c => ({ key: c.key, visible: c.def }))
// Merge a saved layout with the canonical set: honor the user's order + visibility
// for known keys, drop unknown keys, and append any NEW canonical column (added in
// a later release) at the end with its default visibility.
function mergeCols(saved) {
  if (!Array.isArray(saved) || !saved.length) return defaultCols()
  const byKey = new Map(ALL_COLUMNS.map(c => [c.key, c]))
  const seen = new Set(); const out = []
  for (const c of saved) {
    if (c && byKey.has(c.key) && !seen.has(c.key)) { seen.add(c.key); out.push({ key: c.key, visible: c.visible !== false }) }
  }
  for (const c of ALL_COLUMNS) if (!seen.has(c.key)) out.push({ key: c.key, visible: c.def })
  return out
}

const COUNTRY_NAMES = { CA: 'Canada', US: 'United States', GB: 'United Kingdom', IE: 'Ireland', MX: 'Mexico', PT: 'Portugal', ES: 'Spain' }
const countryName = code => code ? (COUNTRY_NAMES[String(code).toUpperCase()] || String(code).toUpperCase()) : '—'

function colLabel(key, t) {
  switch (key) {
    case 'quote':        return t('sp_ctq_col_quote', 'Quote')
    case 'trip':         return t('sp_ctq_col_trip', 'Trip')
    case 'customer':     return t('sp_ctq_col_customer', 'Customer')
    case 'city':         return t('sp_ctq_col_city', 'City')
    case 'region':       return t('sp_ctq_col_region', 'State / Province')
    case 'country':      return t('sp_ctq_col_country', 'Country')
    case 'dateReceived': return t('sp_ctq_col_date_received', 'Date Received')
    case 'dateBooked':   return t('sp_ctq_col_booked', 'Date of Booking')
    case 'tripDate':     return t('sp_ctq_col_trip_date', 'Date of Trip')
    case 'total':        return t('sp_ctq_col_total', 'Total')
    case 'paid':         return t('sp_ctq_col_paid', 'Paid')
    case 'currency':     return t('sp_ctq_col_currency', 'Currency')
    case 'status':       return t('sp_ctq_col_status', 'Status')
    default:             return key
  }
}

// Status cell: the pipeline badge + (for paid trips) a live booking-health badge
// + a cancellation-requested flag. Pulled out so the dynamic table can render it
// for the 'status' column key.
function renderStatusCell(q, t, isCancelPending) {
  const [bg, fg, label] = STATUS[q.status] || ['#f1f5f9', '#475569', q.status]
  return (
    <>
      <span style={{ fontSize: 11, fontWeight: 700, padding: '3px 9px', borderRadius: 999, background: bg, color: fg }}>{label}</span>
      {isCancelPending(q) && <span style={{ marginLeft: 6, fontSize: 11, fontWeight: 700, padding: '3px 9px', borderRadius: 999, background: '#fee2e2', color: '#b91c1c' }}>⚠ {t('sp_ctq_cancel_requested_badge', 'Cancellation requested')}</span>}
      {q.status === 'accepted_paid' && q.bookingHealth && (() => {
        const h = q.bookingHealth
        const [cbg, cfg, txt] = h.failed > 0
          ? ['#fee2e2', '#b91c1c', t('sp_ctq_health_attention', '⚠ {n} need attention', { n: h.failed })]
          : h.deferred > 0
            ? ['#ede9fe', '#6d28d9', t('sp_ctq_health_scheduled', '{n} scheduled', { n: h.deferred })]
            : (h.total > 0 && h.confirmed === h.total)
              ? ['#dcfce7', '#166534', t('sp_ctq_health_all', 'All booked')]
              : ['#fef3c7', '#92400e', t('sp_ctq_health_booking', 'Booking…')]
        return <span style={{ marginLeft: 6, fontSize: 11, fontWeight: 700, padding: '3px 9px', borderRadius: 999, background: cbg, color: cfg }}>{txt}</span>
      })()}
    </>
  )
}

// Columns picker: a dropdown with a checkbox + drag handle per column. Toggles
// visibility and reorders (native HTML5 drag). Changes bubble up via onChange
// (which persists per-user); "Reset to default" restores today's columns.
function ColumnsPicker({ cols, onChange, onReset, t }) {
  const [open, setOpen] = useState(false)
  const dragFrom = useRef(null)
  const ref = useRef(null)
  useEffect(() => {
    if (!open) return
    const onDoc = e => { if (ref.current && !ref.current.contains(e.target)) setOpen(false) }
    document.addEventListener('mousedown', onDoc)
    return () => document.removeEventListener('mousedown', onDoc)
  }, [open])

  function toggle(key) {
    const visibleCount = cols.filter(c => c.visible).length
    const col = cols.find(c => c.key === key)
    if (col && col.visible && visibleCount <= 1) return // keep at least one column
    onChange(cols.map(c => c.key === key ? { ...c, visible: !c.visible } : c))
  }
  function onDragOver(e, i) {
    e.preventDefault()
    const from = dragFrom.current
    if (from == null || from === i) return
    const next = cols.slice()
    const [moved] = next.splice(from, 1)
    next.splice(i, 0, moved)
    dragFrom.current = i
    onChange(next)
  }

  return (
    <div ref={ref} style={{ position: 'relative' }}>
      <button type="button" onClick={() => setOpen(o => !o)}
        style={{ display: 'inline-flex', alignItems: 'center', gap: 6, border: '1px solid #e2e8f0', background: '#fff', color: '#475569', borderRadius: 8, fontSize: 13, fontWeight: 600, padding: '7px 12px', cursor: 'pointer' }}>
        <span style={{ fontSize: 13, color: '#94a3b8' }}>⋮⋮</span>{t('sp_ctq_cols_btn', 'Columns')}
      </button>
      {open && (
        <div style={{ position: 'absolute', right: 0, top: '115%', zIndex: 30, width: 268, background: '#fff', border: '1px solid #e2e8f0', borderRadius: 10, boxShadow: '0 10px 28px rgba(15,23,42,0.14)', padding: 12 }}>
          <div style={{ fontSize: 13, fontWeight: 700, color: '#0f172a' }}>{t('sp_ctq_cols_title', 'Customize columns')}</div>
          <div style={{ fontSize: 11.5, color: '#94a3b8', margin: '2px 0 10px', lineHeight: 1.45 }}>{t('sp_ctq_cols_hint', 'Toggle columns on/off and drag to reorder. Saved to your profile.')}</div>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 1, maxHeight: 340, overflowY: 'auto' }}>
            {cols.map((c, i) => (
              <div key={c.key} draggable
                onDragStart={() => { dragFrom.current = i }}
                onDragOver={e => onDragOver(e, i)}
                onDragEnd={() => { dragFrom.current = null }}
                style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '6px 6px', borderRadius: 6, cursor: 'grab' }}
                onMouseEnter={e => e.currentTarget.style.background = '#f8fafc'}
                onMouseLeave={e => e.currentTarget.style.background = 'transparent'}>
                <span style={{ color: '#cbd5e1', fontSize: 13, userSelect: 'none' }} title="Drag to reorder">⋮⋮</span>
                <input type="checkbox" checked={c.visible} onChange={() => toggle(c.key)} style={{ cursor: 'pointer' }} />
                <span style={{ fontSize: 13, color: '#334155' }}>{colLabel(c.key, t)}</span>
              </div>
            ))}
          </div>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginTop: 10, borderTop: '1px solid #f1f5f9', paddingTop: 8 }}>
            <button type="button" onClick={onReset} style={{ border: 'none', background: 'none', color: '#64748b', fontSize: 12, cursor: 'pointer', textDecoration: 'underline' }}>{t('sp_ctq_cols_reset', 'Reset to default')}</button>
            <button type="button" onClick={() => setOpen(false)} style={{ border: '1px solid #0f172a', background: '#0f172a', color: '#fff', borderRadius: 6, fontSize: 12, fontWeight: 600, padding: '5px 12px', cursor: 'pointer' }}>{t('sp_ctq_cols_done', 'Done')}</button>
          </div>
        </div>
      )}
    </div>
  )
}

export default function CustomTripsList() {
  const { t } = useLanguage()
  const navigate = useNavigate()
  const [tab, setTab] = useState('in_progress')
  const [quotes, setQuotes] = useState([])
  const [loading, setLoading] = useState(true)
  const [creating, setCreating] = useState(false)
  const [err, setErr] = useState(null)
  const [share, setShare] = useState(false)
  const [embedKey, setEmbedKey] = useState(null)
  const [copied, setCopied] = useState('')
  const [search, setSearch] = useState('')
  // Deep-link support: the dashboard "Customer-action items" tile lands here with
  // ?action=requests to open the "Requests to address" filter directly.
  const [searchParams] = useSearchParams()
  const [summaryFilter, setSummaryFilter] = useState(searchParams.get('action') === 'requests' ? 'requests' : null)   // null | 'requests' | 'payments' | 'expiring'
  // Global default: fold the professional fee into the customer's package price.
  // Owner-only to change; seeds the per-quote setting on NEW quotes.
  const [combineFee, setCombineFee] = useState(false)
  const [combineOwner, setCombineOwner] = useState(false)
  const [combineReady, setCombineReady] = useState(false)
  const [savingCombine, setSavingCombine] = useState(false)
  // Per-user column layout (add/remove/reorder). Optimistic from localStorage,
  // then authoritative from the server; saved back to both on every change.
  const [cols, setCols] = useState(defaultCols)
  // Per-user Custom Trips tab order (drag to reorder). null = default order.
  const [tabOrder, setTabOrder] = useState(null)
  const tabDragFrom = useRef(null)
  // Per-quote "who has paid" summaries for the `paid` column (keyed by quote id).
  const [paidSummaries, setPaidSummaries] = useState({})
  // The paid-column hover card: { q, summary, rect } | null. A close timer lets the
  // cursor travel from the cell into the card (which has clickable reminder buttons).
  const [paidPop, setPaidPop] = useState(null)
  const paidCloseTimer = useRef(null)

  useEffect(() => {
    tournamentApi.get('/portal/reseller/quotes')
      .then(r => setQuotes(r.data?.quotes || []))
      .catch(e => setErr(e?.response?.data?.message || e.message))
      .finally(() => setLoading(false))
  }, [])

  useEffect(() => {
    tournamentApi.get('/portal/reseller/quotes/paid-summary')
      .then(r => setPaidSummaries(r.data?.summaries || {}))
      .catch(() => { /* column shows — until it loads */ })
  }, [])

  useEffect(() => {
    tournamentApi.get('/portal/reseller/account/preferences')
      .then(r => { setCombineFee(!!r.data?.combineServiceFeeIntoPackage); setCombineOwner(!!r.data?.isOwner); setCombineReady(true) })
      .catch(() => setCombineReady(false))
  }, [])

  async function saveCombineFee(next) {
    if (!combineReady || !combineOwner) return
    setSavingCombine(true)
    try {
      await tournamentApi.patch('/portal/reseller/account/preferences', { combineServiceFeeIntoPackage: next })
      setCombineFee(next)
    } catch (e) { setErr(e?.response?.data?.message || e.message) }
    finally { setSavingCombine(false) }
  }

  async function createTrip() {
    setCreating(true); setErr(null)
    try {
      const { data } = await tournamentApi.post('/portal/reseller/quotes', {})
      navigate(`/portal/reseller/custom-trips/${data.quote.id}`)
    } catch (e) { setErr(e?.response?.data?.message || e.message); setCreating(false) }
  }

  // Load the saved column layout: optimistic from localStorage, then the server
  // (per-user source of truth). Both are merged with the canonical column set.
  useEffect(() => {
    try { const ls = JSON.parse(localStorage.getItem('ctqCols') || 'null'); if (ls) setCols(mergeCols(ls)) } catch { /* ignore */ }
    tournamentApi.get('/portal/reseller/ui-prefs/ctq-columns')
      .then(r => { if (r.data?.columns) setCols(mergeCols(r.data.columns)) })
      .catch(() => { /* default columns */ })
    // Tab order (per-user): optimistic from localStorage, then the server.
    try { const lt = JSON.parse(localStorage.getItem('ctqTabOrder') || 'null'); if (Array.isArray(lt) && lt.length) setTabOrder(lt) } catch { /* ignore */ }
    tournamentApi.get('/portal/reseller/ui-prefs/ctq-tabs')
      .then(r => { if (Array.isArray(r.data?.tabs) && r.data.tabs.length) setTabOrder(r.data.tabs) })
      .catch(() => { /* default order */ })
  }, [])

  function persistCols(next) {
    setCols(next)
    try { localStorage.setItem('ctqCols', JSON.stringify(next)) } catch { /* ignore */ }
    tournamentApi.patch('/portal/reseller/ui-prefs/ctq-columns', { columns: next }).catch(() => {})
  }
  function resetCols() { persistCols(defaultCols()) }
  function persistTabOrder(nextKeys) {
    setTabOrder(nextKeys)
    try { localStorage.setItem('ctqTabOrder', JSON.stringify(nextKeys)) } catch { /* ignore */ }
    tournamentApi.patch('/portal/reseller/ui-prefs/ctq-tabs', { tabs: nextKeys }).catch(() => {})
  }
  const visibleCols = cols.filter(c => c.visible)

  // Render one cell for a given column key. Inner so it closes over t +
  // isCancelPending (which the Status badge needs). `city`/`state` are parsed
  // once per row and passed in.
  function renderCell(key, q, city, state) {
    switch (key) {
      case 'quote':        return <span style={{ fontFamily: 'monospace', color: '#0f172a' }}>{q.quoteNumber}</span>
      case 'trip':         return q.name || <span style={{ color: '#94a3b8' }}>Untitled</span>
      case 'customer':     return <span style={{ color: '#64748b' }}>{q.masterName || '—'}</span>
      case 'city':         return <span style={{ color: '#64748b' }}>{city || '—'}</span>
      case 'region':       return <span style={{ color: '#64748b' }}>{state || '—'}</span>
      case 'country':      return <span style={{ color: '#64748b' }}>{countryName(q.centerCountryCode)}</span>
      case 'dateReceived': return <span style={{ color: '#64748b', whiteSpace: 'nowrap' }}>{fmtDate(q.createdAt)}</span>
      case 'dateBooked':   return <span style={{ color: '#64748b', whiteSpace: 'nowrap' }}>{fmtDate(q.acceptedAt)}</span>
      case 'tripDate':     return <span style={{ color: '#64748b', whiteSpace: 'nowrap' }}>{fmtDate(q.tripStartDate)}</span>
      case 'total':        return money(q.itemsTotal, q.currency)
      case 'paid': {
        const s = paidSummaries[q.id]
        if (!s || !s.applicable || !s.total) return <span style={{ color: '#cbd5e1' }}>—</span>
        const done = s.count === s.total
        const color = done ? '#166534' : (s.partial ? '#92400e' : '#475569')
        return (
          <span
            onMouseEnter={e => openPaidPop(e, q, s)}
            onMouseLeave={schedulePaidClose}
            style={{ display: 'inline-flex', alignItems: 'center', fontWeight: 700, fontVariantNumeric: 'tabular-nums',
              color, cursor: 'default', borderBottom: '1px dotted #cbd5e1', paddingBottom: 1 }}>
            {s.count}/{s.total}
          </span>
        )
      }
      case 'currency':     return <span style={{ color: '#64748b' }}>{q.currency || 'CAD'}</span>
      case 'status':       return renderStatusCell(q, t, isCancelPending)
      default:             return null
    }
  }

  function reload() {
    tournamentApi.get('/portal/reseller/quotes').then(r => setQuotes(r.data?.quotes || [])).catch(() => {})
  }
  async function deleteTrip(e, q) {
    e.stopPropagation()
    if (!window.confirm(t('sp_ctq_delete_confirm', 'Delete {q}? This cannot be undone.', { q: q.quoteNumber }))) return
    try {
      await tournamentApi.delete(`/portal/reseller/quotes/${q.id}`)
      setQuotes(x => x.filter(z => z.id !== q.id))
    } catch (e2) { alert(e2?.response?.data?.message || e2.message) }
  }
  async function resendTrip(e, q) {
    e.stopPropagation()
    try { await tournamentApi.post(`/portal/reseller/quotes/${q.id}/send`); alert(t('sp_ctq_resent', 'Quote re-sent to the customer.')); reload() }
    catch (e2) { alert(e2?.response?.data?.message || e2.message) }
  }

  // ── Paid-column hover card: open on cell hover, keep open while the cursor is on
  //    the card (so the reminder buttons are clickable), close shortly after leaving.
  function openPaidPop(e, q, summary) {
    if (paidCloseTimer.current) { clearTimeout(paidCloseTimer.current); paidCloseTimer.current = null }
    const rect = e.currentTarget.getBoundingClientRect()
    setPaidPop({ q, summary, rect: { top: rect.top, bottom: rect.bottom, left: rect.left, right: rect.right } })
  }
  function schedulePaidClose() {
    if (paidCloseTimer.current) clearTimeout(paidCloseTimer.current)
    paidCloseTimer.current = setTimeout(() => setPaidPop(null), 160)
  }
  function keepPaidPop() {
    if (paidCloseTimer.current) { clearTimeout(paidCloseTimer.current); paidCloseTimer.current = null }
  }
  // Re-send one split golfer their pay-your-share invite (reseller-initiated nudge).
  async function remindGolfer(q, golferId) {
    try {
      const { data } = await tournamentApi.post(`/portal/reseller/quotes/${q.id}/golfers/${golferId}/remind`)
      alert(data?.alreadyPaid ? t('sp_ctq_reminder_already', 'That golfer has already paid.') : t('sp_ctq_reminder_sent', 'Reminder sent.'))
    } catch (e2) { alert(e2?.response?.data?.message || e2.message) }
  }
  // Single-payer nudge = re-send the whole quote/pay link to the customer.
  async function resendQuoteFromPop(q) {
    try { await tournamentApi.post(`/portal/reseller/quotes/${q.id}/send`); alert(t('sp_ctq_resent', 'Quote re-sent to the customer.')) }
    catch (e2) { alert(e2?.response?.data?.message || e2.message) }
  }
  async function duplicateTrip(e, q) {
    e.stopPropagation()
    try { const { data } = await tournamentApi.post(`/portal/reseller/quotes/${q.id}/duplicate`); navigate(`/portal/reseller/custom-trips/${data.quote.id}`) }
    catch (e2) { alert(e2?.response?.data?.message || e2.message) }
  }
  async function cancelTrip(e, q) {
    e.stopPropagation()
    if (!window.confirm(t('sp_ctq_cancel_quote_confirm', 'Cancel {q}? The customer will no longer be able to accept it.', { q: q.quoteNumber }))) return
    try { await tournamentApi.post(`/portal/reseller/quotes/${q.id}/cancel`); reload() }
    catch (e2) { alert(e2?.response?.data?.message || e2.message) }
  }

  // Status tabs for the quote pipeline + the separate "Upcoming Trips" bucket
  // (fully paid). "All" = everything still in the pipeline (not fully paid),
  // so expired/cancelled quotes remain reachable there.
  const TABS = [
    { key: 'requested',        label: t('sp_ctq_tab_requested', 'Requests'),                match: q => q.status === 'requested' },
    { key: 'in_progress',      label: t('sp_ctq_tab_in_progress', 'In progress'),          match: q => q.status === 'in_progress' },
    { key: 'sent',             label: t('sp_ctq_tab_sent', 'Sent'),                         match: q => q.status === 'sent' },
    { key: 'change_requested', label: t('sp_ctq_tab_change_requested', 'Change requested'), match: q => q.status === 'change_requested' },
    // Cross-cutting: a declined auto-charge or an overdue scheduled payment.
    // (These are also under Partial paid; this tab surfaces the troubled ones.)
    { key: 'payment_issues',   label: t('sp_ctq_tab_payment_issues', 'Payment issues'),     match: q => q.paymentIssues > 0 },
    { key: 'accepted_partial', label: t('sp_ctq_tab_partial', 'Partial paid'),              match: q => q.status === 'accepted_partial' },
    { key: 'all',              label: t('sp_ctq_tab_all', 'All'),                           match: q => q.status !== 'accepted_paid' },
    { key: 'upcoming',         label: t('sp_ctq_tab_upcoming', 'Upcoming Trips'),           match: q => q.status === 'accepted_paid', divider: true },
  ]
  // Apply the saved per-user tab order; any tab not in the saved list (e.g. one
  // added in a later release) appends at the end so it still shows.
  const hasCustomOrder = Array.isArray(tabOrder) && tabOrder.length > 0
  const orderedTabs = hasCustomOrder
    ? (() => {
        const byKey = Object.fromEntries(TABS.map(x => [x.key, x]))
        const head = tabOrder.map(k => byKey[k]).filter(Boolean)
        const tail = TABS.filter(x => !tabOrder.includes(x.key))
        return [...head, ...tail]
      })()
    : TABS
  const sl = search.trim().toLowerCase()
  const activeTab = orderedTabs.find(x => x.key === tab) || orderedTabs[0]

  // At-a-glance alert cards. Clicking a card with a `tabKey` jumps to that tab
  // (Payment issues has its own tab); the others apply a quick filter.
  // A customer asked to cancel an accepted trip and it hasn't been resolved
  // (refunded) yet — an action item the reseller has to act on.
  const isCancelPending = q => !!q.cancelRequestedAt && !q.refundedAt && ['accepted_partial', 'accepted_paid'].includes(q.status)
  const SUMMARY = [
    { key: 'requests', label: t('sp_ctq_sum_requests', 'Requests to address'), match: q => q.status === 'requested' || q.openRequests > 0 || isCancelPending(q), bg: '#fffbeb', bd: '#fde68a', fg: '#92400e' },
    { key: 'payments', label: t('sp_ctq_sum_payments', 'Payment issues'),      match: q => q.paymentIssues > 0, bg: '#fef2f2', bd: '#fecaca', fg: '#991b1b', tabKey: 'payment_issues' },
    { key: 'expiring', label: t('sp_ctq_sum_expiring', 'Expiring soon'),        match: q => { const d = daysUntil(q.validUntil); return q.status === 'sent' && d != null && d >= 0 && d <= 7 }, bg: '#eff6ff', bd: '#bfdbfe', fg: '#1e40af' },
  ]
  const activeSummary = SUMMARY.find(s => s.key === summaryFilter) || null

  // Row set precedence: search (spans all) > a clicked summary card > the active tab.
  const rows = sl ? quotes.filter(q => matchesSearch(q, sl))
    : activeSummary ? quotes.filter(activeSummary.match)
      : quotes.filter(q => activeTab.match(q))
  // Quotes in a tab (respecting the active search) + its count and summed trip
  // value, so each phase shows a running total (Bryce's ask). Totals sum the
  // same figure the Total column shows (itemsTotal). Currency: use the shared
  // currency when a tab's quotes agree, else fall back to CAD (all live data is
  // CAD today; the per-quote currency only diverges once USD trips exist).
  const matchedFor = tb => quotes.filter(q => tb.match(q) && matchesSearch(q, sl))
  const countFor = tb => matchedFor(tb).length
  const totalFor = tb => matchedFor(tb).reduce((s, q) => s + Number(q.itemsTotal || 0), 0)
  const curFor = tb => { const c = [...new Set(matchedFor(tb).map(q => q.currency || 'CAD'))]; return c.length === 1 ? c[0] : 'CAD' }
  // Which tab is highlighted: none while a search or summary filter is active.
  const highlightKey = sl ? 'all' : (summaryFilter ? null : tab)

  async function openShare() {
    setShare(true)
    if (embedKey && embedKey !== '__error__') return // already loaded a real key
    setEmbedKey(null) // show loading (also lets a previous error retry)
    try {
      const { data } = await tournamentApi.get('/portal/reseller/quotes/request-embed')
      setEmbedKey(data.key)
    } catch { setEmbedKey('__error__') }
  }

  return (
    <ResellerShell title={t('sp_nav_custom_trips', 'Custom Trips')}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 20, gap: 24, flexWrap: 'wrap' }}>
        <p style={{ color: '#64748b', maxWidth: 680, margin: 0, fontSize: 14, lineHeight: 1.5 }}>
          {t('sp_ctq_intro', 'Build a custom trip for a customer, send them a quote, and manage payment (pay in full, installments, or split between golfers). Fully-paid trips move to Upcoming Trips, where we keep checking that the golf and hotels stay booked.')}
        </p>
        <div style={{ flexShrink: 0, display: 'flex', gap: 8, flexWrap: 'wrap' }}>
          <button onClick={openShare}
            style={{ padding: '10px 16px', borderRadius: 8, fontWeight: 600, fontSize: 14, border: '1px solid #cbd5e1', background: '#fff', color: '#0f172a', cursor: 'pointer' }}>
            {t('sp_ctq_request_form', 'Request form')}
          </button>
          <button onClick={createTrip} disabled={creating}
            style={{ padding: '10px 16px', borderRadius: 8, fontWeight: 600, fontSize: 14, border: 'none', background: '#0f172a', color: '#fff', cursor: creating ? 'default' : 'pointer', opacity: creating ? 0.6 : 1 }}>
            {creating ? t('sp_ctq_creating', 'Creating…') : t('sp_ctq_new', '+ New custom trip')}
          </button>
        </div>
      </div>

      {share && <RequestFormPanel t={t} embedKey={embedKey} copied={copied} setCopied={setCopied} onClose={() => setShare(false)} />}

      {combineReady && (
        <div style={{ display: 'flex', alignItems: 'flex-start', gap: 12, padding: '10px 14px', border: '1px solid #e2e8f0', borderRadius: 10, background: '#fff', marginBottom: 16, maxWidth: 680 }}>
          <div style={{ marginTop: 1 }}>
            <Toggle checked={combineFee} disabled={!combineOwner || savingCombine} onChange={saveCombineFee} />
          </div>
          <span style={{ fontSize: 13, color: '#475569', lineHeight: 1.45 }}>
            <span style={{ fontWeight: 600, color: '#0f172a' }}>{t('sp_ctq_combine_fee_global', 'Combine the professional fee into the package price shown to customers')}</span>
            <span style={{ display: 'block', fontSize: 12, color: '#94a3b8' }}>
              {t('sp_ctq_combine_fee_global_help', 'New quotes default to this. Customers see one combined price; you always see the fee itemized here.')}
              {!combineOwner ? ' ' + t('sp_ctq_owner_only', '(owner only)') : ''}
            </span>
          </span>
        </div>
      )}

      {err && <div style={{ padding: 10, background: '#fef2f2', border: '1px solid #fecaca', borderRadius: 8, color: '#991b1b', fontSize: 13, marginBottom: 16 }}>{err}</div>}

      {/* At-a-glance alert cards — click to filter the list to those quotes. */}
      {!loading && quotes.length > 0 && (
        <div style={{ display: 'flex', gap: 10, marginBottom: 18, flexWrap: 'wrap' }}>
          {SUMMARY.map(s => {
            const n = quotes.filter(s.match).length
            const on = n > 0
            // A card either jumps to its tab or toggles a quick filter.
            const active = s.tabKey ? (!sl && !summaryFilter && tab === s.tabKey) : (summaryFilter === s.key)
            const onClick = s.tabKey
              ? () => { setSearch(''); setSummaryFilter(null); setTab(s.tabKey) }
              : () => { setSearch(''); setSummaryFilter(active ? null : s.key) }
            return (
              <button key={s.key} disabled={!on}
                onClick={onClick}
                style={{ flex: '1 1 170px', minWidth: 150, textAlign: 'left', padding: '10px 14px', borderRadius: 10,
                  cursor: on ? 'pointer' : 'default',
                  background: on ? s.bg : '#f8fafc',
                  border: `1px solid ${active ? s.fg : (on ? s.bd : '#e2e8f0')}`,
                  boxShadow: active ? `0 0 0 1px ${s.fg}` : 'none' }}>
                <div style={{ fontSize: 22, fontWeight: 800, color: on ? s.fg : '#94a3b8', lineHeight: 1 }}>{n}</div>
                <div style={{ fontSize: 12.5, color: on ? s.fg : '#94a3b8', marginTop: 4 }}>{s.label}</div>
              </button>
            )
          })}
        </div>
      )}

      <div style={{ display: 'flex', gap: 12, alignItems: 'center', justifyContent: 'space-between', borderBottom: '1px solid #e2e8f0', marginBottom: 20, flexWrap: 'wrap' }}>
        <div style={{ display: 'flex', gap: 4, alignItems: 'center', flexWrap: 'wrap' }}>
          {/* Tabs are drag-and-drop reorderable; the order saves per user (like the
              columns). Dragging reorders live + persists; a plain click still just
              switches tab. The divider only shows in the default order. */}
          {orderedTabs.map((tb, i) => (
            <span key={tb.key} draggable
              onDragStart={() => { tabDragFrom.current = i }}
              onDragOver={e => {
                e.preventDefault()
                const from = tabDragFrom.current
                if (from == null || from === i) return
                const keys = orderedTabs.map(x => x.key)
                const [moved] = keys.splice(from, 1)
                keys.splice(i, 0, moved)
                tabDragFrom.current = i
                persistTabOrder(keys)
              }}
              onDragEnd={() => { tabDragFrom.current = null }}
              style={{ display: 'inline-flex', alignItems: 'center', cursor: 'grab' }} title={t('sp_ctq_tab_drag', 'Drag to reorder')}>
              {tb.divider && !hasCustomOrder && <span style={{ width: 1, height: 18, background: '#e2e8f0', margin: '0 8px' }} />}
              {/* Searching spans every status ("All" active); a summary card
                  overrides the tabs. Clicking a tab clears both and browses it. */}
              <TabButton active={highlightKey === tb.key} onClick={() => { setSearch(''); setSummaryFilter(null); setTab(tb.key) }}
                subtotal={countFor(tb) > 0 ? money(totalFor(tb), curFor(tb)) : null}>{tb.label} ({countFor(tb)})</TabButton>
            </span>
          ))}
        </div>
        <div style={{ display: 'flex', gap: 8, alignItems: 'center', marginBottom: 6 }}>
          <ColumnsPicker cols={cols} onChange={persistCols} onReset={resetCols} t={t} />
          <div style={{ position: 'relative' }}>
            <input value={search} onChange={e => { setSearch(e.target.value); if (e.target.value) setSummaryFilter(null) }}
              placeholder={t('sp_ctq_search_ph', 'Search name, email, or phone…')}
              style={{ width: 240, maxWidth: '100%', padding: '7px 28px 7px 12px', border: '1px solid #e2e8f0', borderRadius: 8, fontSize: 13 }} />
            {search && (
              <button onClick={() => setSearch('')} title={t('sp_ctq_search_clear', 'Clear')}
                style={{ position: 'absolute', right: 6, top: '50%', transform: 'translateY(-50%)', border: 'none', background: 'none', color: '#94a3b8', cursor: 'pointer', fontSize: 15, lineHeight: 1 }}>&times;</button>
            )}
          </div>
        </div>
      </div>

      {loading ? <div style={{ padding: 20, color: '#64748b' }}>Loading…</div>
        : rows.length === 0 ? (
          <EmptyState
            title={sl ? t('sp_ctq_empty_search_title', 'No matches')
              : activeSummary ? t('sp_ctq_empty_summary_title', 'All clear')
                : tab === 'payment_issues' ? t('sp_ctq_empty_payment_title', 'No payment issues')
                  : (tab === 'upcoming' ? t('sp_ctq_empty_upcoming_title', 'No upcoming trips yet') : t('sp_ctq_empty_status_title', 'No {label} quotes', { label: activeTab.label.toLowerCase() }))}
            body={sl ? t('sp_ctq_empty_search_body', 'No quotes match "{q}". Clear the search to browse by status.', { q: search.trim() })
              : tab === 'payment_issues' ? t('sp_ctq_empty_payment_body', 'All scheduled payments are on track — nothing to chase.')
              : activeSummary ? t('sp_ctq_empty_summary_body', 'Nothing under "{label}" right now.', { label: activeSummary.label.toLowerCase() })
                : (tab === 'upcoming'
                  ? t('sp_ctq_empty_upcoming_body', 'Once a custom trip is fully paid and booked, it shows up here with a live booking-health check on its hotels and tee times.')
                  : t('sp_ctq_empty_quotes_body2', 'Quotes you build for customers appear here. Use the tabs to filter by status.'))}
          />
        ) : (
          <div style={{ border: '1px solid #eef2f7', borderRadius: 10, overflow: 'hidden' }}>
            <table style={{ width: '100%', borderCollapse: 'collapse' }}>
              <thead><tr style={{ background: '#f8fafc' }}>
                {visibleCols.map(c => <th key={c.key} style={thc}>{colLabel(c.key, t)}</th>)}
                <th style={thc}></th>
              </tr></thead>
              <tbody>
                {rows.map(q => {
                  const { city, state } = parseCityState(q.centerLabel)
                  return (
                    <tr key={q.id} onClick={() => navigate(`/portal/reseller/custom-trips/${q.id}`)}
                      style={{ cursor: 'pointer', borderTop: '1px solid #f1f5f9' }}
                      onMouseEnter={e => e.currentTarget.style.background = '#f8fafc'}
                      onMouseLeave={e => e.currentTarget.style.background = '#fff'}>
                      {visibleCols.map(c => (
                        <td key={c.key} style={{ padding: '10px 12px', fontSize: 13, color: '#334155' }}>{renderCell(c.key, q, city, state)}</td>
                      ))}
                      <td style={{ padding: '10px 12px', textAlign: 'right', whiteSpace: 'nowrap' }} onClick={e => e.stopPropagation()}>
                        {(q.status === 'sent' || q.status === 'change_requested') && (
                          <button onClick={e => resendTrip(e, q)} style={aBtn} title={t('sp_ctq_resend_tip', 'Re-send to customer')}>{t('sp_ctq_resend', 'Resend')}</button>
                        )}
                        <button onClick={e => duplicateTrip(e, q)} style={aBtn} title={t('sp_ctq_duplicate_tip', 'Copy into a new draft')}>{t('sp_ctq_duplicate', 'Duplicate')}</button>
                        {['in_progress', 'sent', 'change_requested'].includes(q.status) && (
                          <button onClick={e => cancelTrip(e, q)} style={{ ...aBtn, color: '#b91c1c', borderColor: '#fecaca' }} title={t('sp_ctq_cancel_tip', 'Cancel this quote')}>{t('sp_ctq_cancel', 'Cancel')}</button>
                        )}
                        <button onClick={e => deleteTrip(e, q)} title={t('sp_ctq_delete', 'Delete')}
                          style={{ border: 'none', background: 'none', color: '#cbd5e1', cursor: 'pointer', fontSize: 16, lineHeight: 1, marginLeft: 6, verticalAlign: 'middle' }}
                          onMouseEnter={e => e.currentTarget.style.color = '#ef4444'} onMouseLeave={e => e.currentTarget.style.color = '#cbd5e1'}>🗑</button>
                      </td>
                    </tr>
                  )
                })}
              </tbody>
            </table>
          </div>
        )}

      {paidPop && (
        <PaidPopover pop={paidPop} onKeep={keepPaidPop} onClose={schedulePaidClose}
          onRemindGolfer={remindGolfer} onResendQuote={resendQuoteFromPop} t={t} />
      )}
    </ResellerShell>
  )
}

// Hover card for the `paid` column: every payer, each of their payment lines
// (deposit / installments) with status, and a reminder nudge for anyone not yet
// settled. position:fixed (computed off the cell rect) so it escapes the table's
// overflow:hidden clip.
function PaidPopover({ pop, onKeep, onClose, onRemindGolfer, onResendQuote, t }) {
  const { q, summary, rect } = pop
  const W = 300
  let left = rect.left
  if (left + W > window.innerWidth - 12) left = Math.max(12, window.innerWidth - W - 12)
  const top = Math.min(rect.bottom + 6, window.innerHeight - 40)
  return (
    <div onMouseEnter={onKeep} onMouseLeave={onClose}
      style={{ position: 'fixed', top, left, width: W, zIndex: 60, background: '#fff', border: '1px solid #e2e8f0',
        borderRadius: 10, boxShadow: '0 14px 36px rgba(15,23,42,0.2)', padding: 12, maxHeight: '60vh', overflowY: 'auto' }}>
      <div style={{ fontSize: 12, fontWeight: 700, color: '#0f172a', marginBottom: 2 }}>{t('sp_ctq_paid_pop_title', 'Payment status')}</div>
      <div style={{ fontSize: 11.5, color: '#64748b', marginBottom: 8 }}>
        {t('sp_ctq_paid_settled_n', '{count} of {total} fully paid', { count: summary.count, total: summary.total })}
      </div>
      {summary.payers.map((p, i) => (
        <div key={p.golferId ?? `solo${i}`} style={{ borderTop: '1px solid #f1f5f9', padding: '8px 0' }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', gap: 8 }}>
            <div style={{ minWidth: 0 }}>
              <div style={{ fontSize: 12.5, fontWeight: 600, color: '#0f172a' }}>
                {p.name || t('sp_ctq_paid_customer', 'Customer')}
                {p.isLead && <span style={{ fontSize: 11, fontWeight: 600, color: '#64748b' }}>{t('sp_ctq_paid_lead', ' · lead')}</span>}
              </div>
              {p.email && <div style={{ fontSize: 11, color: '#94a3b8', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{p.email}</div>}
            </div>
            <PayerBadge settled={p.settled} covered={p.coveredByLead} t={t} />
          </div>
          {p.lines && p.lines.length > 0 && (
            <div style={{ marginTop: 5 }}>
              {p.lines.map((ln, j) => (
                <div key={j} style={{ display: 'flex', justifyContent: 'space-between', gap: 8, fontSize: 11, padding: '1px 0' }}>
                  <span style={{ color: '#64748b' }}>{lineLabel(ln, t)}{ln.when ? ` · ${fmtDate(ln.when)}` : ''}</span>
                  <span style={{ whiteSpace: 'nowrap' }}>
                    <span style={{ color: '#334155' }}>{money(ln.amount, q.currency)}</span>
                    <span style={{ color: lineColor(ln.status), fontWeight: 700 }}> · {lineStatusLabel(ln.status, t)}</span>
                  </span>
                </div>
              ))}
            </div>
          )}
          {!p.settled && (
            <button type="button"
              onClick={() => (p.canRemind ? onRemindGolfer(q, p.golferId) : onResendQuote(q))}
              style={{ marginTop: 6, border: '1px solid #e2e8f0', background: '#fff', color: '#0f766e', borderRadius: 6, fontSize: 11.5, fontWeight: 700, padding: '4px 10px', cursor: 'pointer' }}>
              {p.canRemind ? t('sp_ctq_send_reminder', 'Send reminder') : t('sp_ctq_resend_quote', 'Resend quote')}
            </button>
          )}
        </div>
      ))}
    </div>
  )
}

function PayerBadge({ settled, covered, t }) {
  const s = settled
    ? { bg: '#dcfce7', fg: '#166534', label: t('sp_ctq_paid_badge_paid', 'Paid') }
    : covered
      ? { bg: '#f1f5f9', fg: '#475569', label: t('sp_ctq_paid_badge_covered', 'Covered by lead') }
      : { bg: '#fff7ed', fg: '#9a3412', label: t('sp_ctq_paid_badge_outstanding', 'Outstanding') }
  return <span style={{ flexShrink: 0, fontSize: 10.5, fontWeight: 700, padding: '2px 7px', borderRadius: 999, background: s.bg, color: s.fg }}>{s.label}</span>
}

function lineLabel(ln, t) {
  if (ln.bucket === 'full') return t('sp_ctq_pl_full', 'Full payment')
  if (ln.bucket === 'deposit') return t('sp_ctq_pl_deposit', 'Deposit')
  if (ln.installmentNo != null) return t('sp_ctq_pl_installment', 'Installment {n}', { n: ln.installmentNo })
  return ln.bucket || t('sp_ctq_pl_payment', 'Payment')
}
function lineStatusLabel(status, t) {
  switch (status) {
    case 'paid':       return t('sp_ctq_pl_paid', 'paid')
    case 'authorized': return t('sp_ctq_pl_auth', 'authorized')
    case 'scheduled':  return t('sp_ctq_pl_scheduled', 'scheduled')
    case 'declined':   return t('sp_ctq_pl_declined', 'declined')
    default:           return status
  }
}
function lineColor(status) {
  return status === 'paid' ? '#166534' : status === 'declined' ? '#b91c1c' : status === 'authorized' ? '#1e40af' : '#92400e'
}

function TabButton({ active, onClick, children, subtotal }) {
  return (
    <button onClick={onClick} style={{
      padding: '7px 14px', fontSize: 14, fontWeight: 600, cursor: 'pointer', textAlign: 'center',
      background: 'none', border: 'none', borderBottom: `2px solid ${active ? '#0f172a' : 'transparent'}`,
      color: active ? '#0f172a' : '#64748b', marginBottom: -1,
    }}>
      <span style={{ display: 'block' }}>{children}</span>
      {subtotal != null && (
        <span style={{ display: 'block', fontSize: 11, fontWeight: 600, color: active ? '#334155' : '#94a3b8', marginTop: 1 }}>{subtotal}</span>
      )}
    </button>
  )
}

function EmptyState({ title, body }) {
  return (
    <div style={{ padding: '40px 24px', textAlign: 'center', border: '1px dashed #cbd5e1', borderRadius: 12, background: '#f8fafc' }}>
      <div style={{ fontSize: 15, fontWeight: 600, color: '#334155', marginBottom: 6 }}>{title}</div>
      <div style={{ fontSize: 13, color: '#94a3b8', maxWidth: 460, margin: '0 auto', lineHeight: 1.5 }}>{body}</div>
    </div>
  )
}

// Share panel for the account-level Custom Trip Request embed (CTR). Shows the
// direct link + the auto-resizing iframe snippet to drop on the reseller's site.
function RequestFormPanel({ t, embedKey, copied, setCopied, onClose }) {
  const loading = !embedKey
  const errored = embedKey === '__error__'
  const url = embedKey && !errored ? `${window.location.origin}/embed/custom-trip/${embedKey}` : ''
  const snippet = url ? `<iframe id="integolf-trip-request" src="${url}" width="100%" scrolling="no" frameborder="0" allowtransparency="true" style="border:none;max-width:820px;width:100%;min-height:600px;display:block;margin:0 auto;background:transparent;"></iframe>
<script>
(function(){
  var f=document.getElementById('integolf-trip-request');
  window.addEventListener('message',function(e){
    if(!f||!e.data||e.data.type!=='integolf-embed-resize')return;
    if(f.contentWindow&&e.source!==f.contentWindow)return;
    var h=e.data.height; if(typeof h==='number'&&h>0){f.style.height=h+'px';}
  });
})();
</script>` : ''
  const copy = (val, which) => { try { navigator.clipboard?.writeText(val); setCopied(which); setTimeout(() => setCopied(''), 1500) } catch {} }
  const box = { background: '#0b1220', color: '#c7d2fe', borderRadius: 8, padding: '10px 12px', fontSize: 12, fontFamily: 'monospace', wordBreak: 'break-all', whiteSpace: 'pre-wrap', flex: 1 }
  const copyBtn = (which) => ({ padding: '6px 12px', borderRadius: 6, border: '1px solid #cbd5e1', background: '#fff', fontSize: 12, fontWeight: 600, cursor: 'pointer', flexShrink: 0, color: copied === which ? '#15803d' : '#0f172a' })

  return (
    <div style={{ background: '#fff', border: '1px solid #e2e8f0', borderRadius: 12, padding: 18, marginBottom: 18 }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 12 }}>
        <div>
          <div style={{ fontSize: 15, fontWeight: 700, color: '#0f172a' }}>{t('sp_ctq_request_form_title', 'Custom trip request form')}</div>
          <div style={{ fontSize: 13, color: '#64748b', marginTop: 3, maxWidth: 640, lineHeight: 1.5 }}>
            {t('sp_ctq_request_form_sub', 'Drop this on your website (or share the link) so customers can request a custom trip. Requests land here in the “Requested” list for you to build.')}
          </div>
        </div>
        <button onClick={onClose} style={{ border: 'none', background: 'none', color: '#94a3b8', cursor: 'pointer', fontSize: 18 }} title="Close">✕</button>
      </div>

      {loading && <div style={{ color: '#94a3b8', fontSize: 13, marginTop: 14 }}>{t('sp_loading', 'Loading…')}</div>}
      {errored && <div style={{ color: '#b91c1c', fontSize: 13, marginTop: 14 }}>{t('sp_ctq_request_form_err', 'Could not load your request-form link. Please try again.')}</div>}

      {url && (
        <div style={{ marginTop: 14, display: 'flex', flexDirection: 'column', gap: 14 }}>
          <div>
            <div style={{ fontSize: 12, fontWeight: 600, color: '#475569', marginBottom: 6 }}>{t('sp_ctq_direct_link', 'Direct link')}</div>
            <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
              <div style={box}>{url}</div>
              <button style={copyBtn('link')} onClick={() => copy(url, 'link')}>{copied === 'link' ? t('sp_copied', 'Copied') : t('sp_copy', 'Copy')}</button>
              <a href={url} target="_blank" rel="noopener" style={{ ...copyBtn('open'), textDecoration: 'none', display: 'inline-block' }}>{t('sp_open', 'Open')}</a>
            </div>
          </div>
          <div>
            <div style={{ fontSize: 12, fontWeight: 600, color: '#475569', marginBottom: 6 }}>{t('sp_ctq_iframe_code', 'Iframe code (paste into your site)')}</div>
            <div style={{ display: 'flex', gap: 8, alignItems: 'flex-start' }}>
              <div style={box}>{snippet}</div>
              <button style={copyBtn('iframe')} onClick={() => copy(snippet, 'iframe')}>{copied === 'iframe' ? t('sp_copied', 'Copied') : t('sp_copy', 'Copy')}</button>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}
